import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../contexts/StoreContext';
import {
  getInventoriesForPrepOrdering,
  calculatePrepSuggestions,
  sendPrepEmail
} from '../services/prepOrderingService';
import { FaSpinner, FaSearch, FaTimes, FaFlask, FaPaperPlane } from 'react-icons/fa';
import './css_pages/PrepOrdering.css';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${m}/${day}/${y}`;
};

const fmt = (v, dec = 2) => {
  const n = parseFloat(v);
  if (isNaN(n)) return (0).toFixed(dec);
  return n.toFixed(dec);
};

function PrepOrdering() {
  const { selectedStore } = useStore();

  const [inventories, setInventories]             = useState([]);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [items, setItems]                         = useState([]);
  const [loading, setLoading]                     = useState(false);
  const [hasLoaded, setHasLoaded]                 = useState(false);
  const [search, setSearch]                       = useState('');
  const [onlyNeeded, setOnlyNeeded]               = useState(false);

  // Modal de envío
  const [showSendModal, setShowSendModal] = useState(false);
  const [toEmail, setToEmail]             = useState('');
  const [ccEmail, setCcEmail]             = useState('');
  const [sending, setSending]             = useState(false);

  // ── Inventarios ──
  const fetchInventories = useCallback(async () => {
    if (!selectedStore) return;
    try {
      setLoading(true);
      const r = await getInventoriesForPrepOrdering(selectedStore.id);
      if (r.success) setInventories(r.data);
    } catch {
      alert('Error loading inventories');
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedStore) {
      fetchInventories();
      setItems([]); setSelectedInventory(null); setHasLoaded(false); setSearch('');
    }
  }, [selectedStore, fetchInventories]);

  // ── Calcular al elegir inventario ──
  const handleInventoryChange = async (e) => {
    const inventoryId = e.target.value;
    if (!inventoryId) {
      setSelectedInventory(null); setItems([]); setHasLoaded(false);
      return;
    }
    try {
      setLoading(true);
      const inv = inventories.find(i => i.id_inventories === parseInt(inventoryId));
      setSelectedInventory(inv);
      const r = await calculatePrepSuggestions(parseInt(inventoryId), selectedStore.id);
      if (r.success) {
        setItems(r.data.items || []);
        setHasLoaded(true);
      }
    } catch {
      alert('Error calculating preparation suggestions');
    } finally {
      setLoading(false);
    }
  };

  // ── Editar cantidad real a preparar ──
  const handleActualChange = (idPrep, value) => {
    setItems(prev => prev.map(it =>
      it.id_prep === idPrep ? { ...it, actual_order: value } : it
    ));
  };

  // ── Filtrado ──
  const filtered = items.filter(it => {
    const s = search.trim().toLowerCase();
    if (s && !(it.prep_name || '').toLowerCase().includes(s)) return false;
    if (onlyNeeded && !(parseFloat(it.suggested_order) > 0)) return false;
    return true;
  });

  const neededCount = items.filter(it => parseFloat(it.suggested_order) > 0).length;

  // ── Envío ──
  const openSendModal = () => {
    // Preseleccionar los que tienen sugerencia > 0, con su cantidad
    setShowSendModal(true);
  };

  const itemsToSend = items
    .map(it => {
      const actual = (it.actual_order !== '' && it.actual_order !== null && it.actual_order !== undefined)
        ? parseFloat(it.actual_order) : 0;
      const qty = actual > 0 ? actual : (parseFloat(it.suggested_order) || 0);
      return { ...it, _qtyToSend: qty };
    })
    .filter(it => it._qtyToSend > 0);

  const handleSend = async () => {
    if (!toEmail.trim()) { alert('Please enter a destination email'); return; }
    if (itemsToSend.length === 0) { alert('There are no items with a quantity to prepare'); return; }
    try {
      setSending(true);
      const payload = {
        storeId: selectedStore.id,
        inventoryId: selectedInventory?.id_inventories || null,
        toEmail: toEmail.trim(),
        cc:      ccEmail.trim() || undefined,
        items:   itemsToSend.map(it => ({
          prep_name:       it.prep_name,
          yield_unit:      it.yield_unit,
          stock_on_hand:   it.stock_on_hand,
          par:             it.par,
          suggested_order: it.suggested_order,
          actual_order:    it._qtyToSend
        }))
      };
      const r = await sendPrepEmail(payload);
      if (r.success) {
        alert('Preparation list sent successfully');
        setShowSendModal(false); setToEmail(''); setCcEmail('');
      }
    } catch {
      alert('Error sending the preparation list');
    } finally {
      setSending(false);
    }
  };

  // ── Guards ──
  if (!selectedStore) {
    return (
      <div className="pbo-page">
        <div className="pbo-page-header"><h1 className="pbo-page-title"><FaFlask /> Pre-Batch Ordering</h1></div>
        <div className="pbo-empty"><p>Please select a store to start.</p></div>
      </div>
    );
  }

  return (
    <div className="pbo-page">

      {/* HEADER */}
      <div className="pbo-page-header pbo-sticky-header">
        <div className="pbo-header-left">
          <h1 className="pbo-page-title"><FaFlask /> Pre-Batch Ordering</h1>
          <span className="pbo-store-badge">{selectedStore.name}</span>
        </div>
        {hasLoaded && (
          <button className="pbo-btn-send" onClick={openSendModal} disabled={loading}>
            <FaPaperPlane /> Send to Kitchen
          </button>
        )}
      </div>

      {/* CONTROLS */}
      <div className="pbo-controls">
        <div className="pbo-control-group">
          <label htmlFor="pbo-inv-select">Select Inventory (by date):</label>
          <select
            id="pbo-inv-select"
            onChange={handleInventoryChange}
            disabled={loading || inventories.length === 0}
            className="pbo-select"
            value={selectedInventory?.id_inventories || ''}
          >
            <option value="">-- Select an inventory --</option>
            {inventories.map(inv => (
              <option key={inv.id_inventories} value={inv.id_inventories}>
                {formatDate(inv.inventory_date)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <div className="pbo-loading"><FaSpinner className="pbo-spinner" /><p>Loading…</p></div>
      )}

      {!loading && hasLoaded && (
        <>
          {/* TOOLBAR */}
          <div className="pbo-toolbar">
            <div className="pbo-search">
              <FaSearch className="pbo-search-icon" />
              <input
                type="text"
                placeholder="Search by preparation name…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && <button className="pbo-search-clear" onClick={() => setSearch('')} type="button"><FaTimes /></button>}
            </div>
            <button
              className={`pbo-filter-chip ${onlyNeeded ? 'active' : ''}`}
              onClick={() => setOnlyNeeded(v => !v)}
              type="button"
            >
              Needs preparing
              {neededCount > 0 && <span className="pbo-chip-count">{neededCount}</span>}
            </button>
            <div className="pbo-count-badge">{filtered.length} prep(s)</div>
          </div>

          {/* TABLE */}
          <div className="pbo-table-wrap">
            <table className="pbo-table">
              <thead>
                <tr>
                  <th>Preparation</th>
                  <th className="pbo-num">Stock on Hand</th>
                  <th className="pbo-num">Reorder Point</th>
                  <th className="pbo-num">Par</th>
                  <th className="pbo-num">Suggested</th>
                  <th className="pbo-num">To Prepare</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} className="pbo-no-results">No preparations found</td></tr>
                ) : filtered.map(it => {
                  const needs = parseFloat(it.suggested_order) > 0;
                  return (
                    <tr key={it.id_prep} className={needs ? 'pbo-row-needs' : ''}>
                      <td className="pbo-name-cell">
                        {it.prep_name}
                        {it.is_unconfigured && <span className="pbo-warn-badge">no par/reorder</span>}
                      </td>
                      <td className="pbo-num">{fmt(it.stock_on_hand)} <span className="pbo-unit">{it.yield_unit}</span></td>
                      <td className="pbo-num">{fmt(it.reorder_point)}</td>
                      <td className="pbo-num">{fmt(it.par)}</td>
                      <td className={`pbo-num ${needs ? 'pbo-suggested' : ''}`}>{fmt(it.suggested_order, 0)}</td>
                      <td className="pbo-num">
                        <input
                          type="number" min="0" step="any"
                          className="pbo-actual-input"
                          value={it.actual_order || ''}
                          placeholder="0"
                          onChange={e => handleActualChange(it.id_prep, e.target.value)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && !hasLoaded && (
        <div className="pbo-empty"><p>Select an inventory date above to calculate what needs preparing.</p></div>
      )}

      {/* SEND MODAL */}
      {showSendModal && (
        <div className="pbo-modal-overlay">
          <div className="pbo-modal">
            <div className="pbo-modal-header">
              <h3><FaPaperPlane /> Send Preparation List</h3>
              <button className="pbo-modal-close" onClick={() => setShowSendModal(false)}><FaTimes /></button>
            </div>
            <div className="pbo-modal-body">
              <div className="pbo-form-group">
                <label>To <span className="pbo-required">*</span></label>
                <input type="email" value={toEmail} onChange={e => setToEmail(e.target.value)} placeholder="kitchen@example.com" />
              </div>
              <div className="pbo-form-group">
                <label>CC (optional)</label>
                <input type="email" value={ccEmail} onChange={e => setCcEmail(e.target.value)} placeholder="cc@example.com" />
              </div>

              <div className="pbo-modal-summary">
                <strong>{itemsToSend.length}</strong> preparation(s) will be sent:
              </div>
              <div className="pbo-modal-list">
                {itemsToSend.length === 0 ? (
                  <p className="pbo-modal-empty">No items with a quantity to prepare. Set a "To Prepare" value or wait for suggestions.</p>
                ) : itemsToSend.map(it => (
                  <div key={it.id_prep} className="pbo-modal-list-row">
                    <span>{it.prep_name}</span>
                    <span className="pbo-modal-qty">{fmt(it._qtyToSend, 2)} {it.yield_unit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="pbo-modal-footer">
              <button className="pbo-btn-secondary" onClick={() => setShowSendModal(false)} disabled={sending}>Cancel</button>
              <button className="pbo-btn-primary" onClick={handleSend} disabled={sending || itemsToSend.length === 0}>
                {sending ? <><FaSpinner className="pbo-spinner" /> Sending…</> : <><FaPaperPlane /> Send</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PrepOrdering;